import axios from 'axios';
import {
  body,
  customEvent,
  setHtmlScrollable,
  setHtmlUnscrollable
} from '@jspartials/general-utils';
import {
  classes,
  addClassToElement,
  removeClassToElement,
  hasClassElement
} from '@jspartials/class-list-utils';
import {
  appendElementContent,
  listenDOMEvent
} from '@jspartials/dom-api-utils';
import { getCookie, setCookie } from '@jspartials/cookie-utils';
import { startSpinner, stopSpinner } from '@jscomponents/spinner';
import { listenProductTiles } from '@jscomponents/product-tile';
import { pushOnDataLayer } from '@jspartials/gtm-utils';

// Selectors
const selector = {
  pdp: {
    wrapper: '.js-pdp',
    similarProductsButton: '.js-pdp__similar-products-button'
  },
  similarProductsPopup: {
    wrapper: '.js-similar-products-popup',
    closeButton: '.js-similar-products-popup__close',
    showMoreButton: '.js-similar-products-popup__button-show-more',
    list: '.js-similar-products-popup__list',
    listItem: '.js-similar-products-popup__list-item',
    selectSort: '.js-similar-products-popup__select-sort'
  },
  price: {
    sales: '.js-product-price__sales'
  }
};

// Elements
const element = {
  pdp: {
    wrapper: document.querySelector(selector.pdp.wrapper) || null,
    similarProductsButton:
      document.querySelector(selector.pdp.similarProductsButton) || null
  }
};

// Variables
const sortBy = {
  relevance: 'relevance',
  priceAsc: 'price_asc',
  priceDesc: 'price_desc'
};
const similarProductsEnabled = globalVariables.similarProductsEnabled || false;

/**
 * Initialize similar products for a given product ID
 * @param {string} productId - The product ID to fetch similar products for
 * @returns {void}
 */
const initSimilarProducts = (productId) => {
  pushOnDataLayer({
    event: 'click_similar_products',
    productId: productId
  });

  fetchSimilarProducts(productId)
    .then((response) => {
      const { data } = response;

      paintSimilarProductsPopup(data);
    })
    .catch((error) => {
      console.error(
        `Error fetching similar products for product ID: ${productId}`,
        error
      );
    })
    .then(() => {
      stopSpinner();
    });
};

/**
 * Fetch similar products from the server
 * @param {string} productId - The product ID to fetch similar products for
 *
 */
const fetchSimilarProducts = (productId) => {
  const formData = new FormData();
  formData.append('productId', productId);

  return axios.post(globalVariables.endpoint.getSimilarProducts, formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  });
};

/**
 * Paint the similar products popup with the provided HTML
 * @param {string} html - The HTML content for the similar products popup
 * @returns {void}
 */
const paintSimilarProductsPopup = (html) => {
  closeSimilarProductsPopup();
  setHtmlUnscrollable();
  body.insertAdjacentHTML('beforeend', html);
  afterPaintSimilarProductsPopup();
};

/**
 *  Perform actions after the similar products popup is painted
 * @returns {void}
 */
const afterPaintSimilarProductsPopup = () => {
  setTimeout(() => {
    unloadSimilarProductsPopup();
  }, 100);
  listenCloseSimilarProductsPopup();
  listenShowMoreSimilarProductsButton();
  listenSelectSortSimilarProductsPopup();
  listenProductTiles();
};

/**
 * Unload the similar products popup by removing the loading class
 * @returns {void}
 */
const unloadSimilarProductsPopup = () => {
  const similarProductsPopup =
    document.querySelector(selector.similarProductsPopup.wrapper) || null;

  if (similarProductsPopup) {
    removeClassToElement(similarProductsPopup, classes.is.loading);
  }
};

/**
 * Listen for the close event on the similar products popup
 * @returns {void}
 */
const listenCloseSimilarProductsPopup = () => {
  const similarProductsPopup =
    document.querySelector(selector.similarProductsPopup.wrapper) || null;
  const similarProductsPopupCloseButton =
    document.querySelector(selector.similarProductsPopup.closeButton) || null;

  if (similarProductsPopup) {
    listenDOMEvent(similarProductsPopup, 'click', (event) => {
      if (event.target === similarProductsPopup) {
        closeSimilarProductsPopup();
      }
    });
  }

  if (similarProductsPopupCloseButton) {
    listenDOMEvent(
      similarProductsPopupCloseButton,
      'click',
      closeSimilarProductsPopup
    );
  }
};

/**
 * Close the similar products popup
 * @returns {void}
 */
const closeSimilarProductsPopup = () => {
  const similarProductsPopup =
    document.querySelector(selector.similarProductsPopup.wrapper) || null;

  if (similarProductsPopup) {
    similarProductsPopup.remove();
    setHtmlScrollable();
  }
};

/**
 * Listen for the show more button click event in the similar products popup
 * @returns {void}
 */
const listenShowMoreSimilarProductsButton = () => {
  const similarProductsPopupShowMoreButton =
    document.querySelector(selector.similarProductsPopup.showMoreButton) ||
    null;

  if (similarProductsPopupShowMoreButton) {
    listenDOMEvent(
      similarProductsPopupShowMoreButton,
      'click',
      showMoreSimilarProducts
    );
  }
};

/**
 * Unhide more similar products in the popup
 * @param {Event} event - The click event
 * @returns {void}
 */
const showMoreSimilarProducts = (event) => {
  const button = event.currentTarget;
  const hiddenItems =
    document.querySelectorAll(
      `${selector.similarProductsPopup.listItem}.${classes.hidden}`
    ) || [];

  hiddenItems.forEach((item) => {
    removeClassToElement(item, classes.hidden);
  });

  button.remove();
};

/**
 * Show similar products buttons on PDP
 * @returns {void}
 */
const showSimilarProductsButtonOnPdp = () => {
  if (similarProductsEnabled) {
    if (element.pdp.similarProductsButton) {
      if (!hasClassElement(element.pdp.wrapper, classes.show.similarProducts)) {
        addClassToElement(element.pdp.wrapper, classes.show.similarProducts);
      }

      listenSimilarProductsButtonsOnPdp();
    } else {
      if (hasClassElement(element.pdp.wrapper, classes.show.similarProducts)) {
        removeClassToElement(element.pdp.wrapper, classes.show.similarProducts);
      }
    }
  }
};

/**
 * Initialize listeners for similar products buttons on PDP
 * @returns {void}
 */
const listenSimilarProductsButtonsOnPdp = () => {
  const similarProductsButtons =
    document.querySelectorAll(selector.pdp.similarProductsButton) || [];

  similarProductsButtons.forEach((button) => {
    listenDOMEvent(button, 'click', clickOnSimilarProductsButtonOnPdp);
  });
};

/**
 * Handle click on similar products button on PDP
 * @param {Event} event - The click event
 * @returns {void}
 */
const clickOnSimilarProductsButtonOnPdp = (e) => {
  e.stopPropagation();
  const productId = element.pdp.wrapper?.dataset?.pid || '';

  startSpinner();

  if (productId) {
    initSimilarProducts(productId);
  } else {
    stopSpinner();
  }
};

/**
 * Listen for changes in the similar products popup select sort element
 * @returns {void}
 */
const listenSelectSortSimilarProductsPopup = () => {
  const selectSortElement =
    document.querySelector(selector.similarProductsPopup.selectSort) || null;

  if (selectSortElement) {
    listenDOMEvent(selectSortElement, 'change', sortSimilarProducts);
  }
};

/**
 * Sort similar products based on the selected option
 * @param {Event} event - The change event
 * @returns {void}
 */
const sortSimilarProducts = (event) => {
  const selectElement = event.currentTarget;
  const selectedValue = selectElement.value;
  const similarProductsList =
    document.querySelector(selector.similarProductsPopup.list) || null;
  const similarProductsListItems =
    document.querySelectorAll(selector.similarProductsPopup.listItem) || [];
  const showMoreButton =
    document.querySelector(selector.similarProductsPopup.showMoreButton) ||
    null;
  const totalItems = similarProductsListItems.length;
  const maxItemsToShow = totalItems / 2;
  let similarProductsListItemsSorted = [];

  if (selectedValue === sortBy.priceAsc) {
    similarProductsListItemsSorted = sortItemsByPrice(
      similarProductsListItems,
      sortBy.priceAsc
    );
  } else if (selectedValue === sortBy.priceDesc) {
    similarProductsListItemsSorted = sortItemsByPrice(
      similarProductsListItems,
      sortBy.priceDesc
    );
  } else {
    similarProductsListItemsSorted = sortItemsByRelevance(
      similarProductsListItems
    );
  }

  if (similarProductsList) {
    similarProductsList.innerHTML = '';
    similarProductsListItemsSorted.forEach((item, index) => {
      removeClassToElement(item, classes.hidden);
      appendElementContent(similarProductsList, item);
      if (showMoreButton) {
        if (index >= maxItemsToShow) {
          addClassToElement(item, classes.hidden);
        }
      }
    });
  }

  afterPaintSimilarProductsPopup();
};

/**
 * Sort similar products items by price
 * @param {NodeList} items - The list of similar products items
 * @param {string} sortOrder - The order to sort by (ascending or descending)
 * @returns {Array} - Sorted array of similar products items
 */
const sortItemsByPrice = (items, sortOrder) => {
  return Array.from(items).sort((a, b) => {
    const priceSalesA = a.querySelector(selector.price.sales) || null;
    const priceSalesB = b.querySelector(selector.price.sales) || null;
    const priceA = priceSalesA
      ? parseFloat(priceSalesA.getAttribute('content'))
      : 0;
    const priceB = priceSalesB
      ? parseFloat(priceSalesB.getAttribute('content'))
      : 0;

    return sortOrder === sortBy.priceAsc ? priceA - priceB : priceB - priceA;
  });
};

/**
 * Sort similar products items by relevance
 * @param {NodeList} items - The list of similar products items
 * @returns {Array} - Sorted array of similar products items by relevance
 */
const sortItemsByRelevance = (items) => {
  return Array.from(items).sort((a, b) => {
    const indexA = parseInt(a.dataset.index, 10) || 0;
    const indexB = parseInt(b.dataset.index, 10) || 0;
    return indexA - indexB;
  });
};

// Initialize the script when the DOM is fully loaded
document.addEventListener('DOMContentLoaded', function () {
  if (element.pdp.wrapper) {
    showSimilarProductsButtonOnPdp();
  }
});

// Listen for updates to the product detail page content
document.addEventListener('afterUpdateContentOfProductDetailPage', function () {
  showSimilarProductsButtonOnPdp();
});
